import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { LISTING_PAGE_PARAM_TYPE_NEW } from '../../util/urlHelpers';
import { listingsAPI } from '../../api';
import { prepareImageForUpload } from '../../util/resizeImage';

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/EditPostPage/SET_INITIAL_STATE';

export const SHOW_POST_REQUEST = 'app/EditPostPage/SHOW_POST_REQUEST';
export const SHOW_POST_SUCCESS = 'app/EditPostPage/SHOW_POST_SUCCESS';
export const SHOW_POST_ERROR = 'app/EditPostPage/SHOW_POST_ERROR';

export const UPDATE_POST_REQUEST = 'app/EditPostPage/UPDATE_POST_REQUEST';
export const UPDATE_POST_SUCCESS = 'app/EditPostPage/UPDATE_POST_SUCCESS';
export const UPDATE_POST_ERROR = 'app/EditPostPage/UPDATE_POST_ERROR';

export const SUBMIT_POST_REQUEST = 'app/EditPostPage/SUBMIT_POST_REQUEST';
export const SUBMIT_POST_SUCCESS = 'app/EditPostPage/SUBMIT_POST_SUCCESS';
export const SUBMIT_POST_ERROR = 'app/EditPostPage/SUBMIT_POST_ERROR';

export const UPLOAD_IMAGE_REQUEST = 'app/EditPostPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/EditPostPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/EditPostPage/UPLOAD_IMAGE_ERROR';

export const REMOVE_UPLOADED_IMAGE = 'app/EditPostPage/REMOVE_UPLOADED_IMAGE';

// ================ Reducer ================ //

const initialState = {
  showPostInProgress: false,
  showPostError: null,
  postId: null,
  updatePostInProgress: false,
  updatePostError: null,
  submitPostInProgress: false,
  submitPostError: null,
  imageUploadInProgress: false,
  imageUploadError: null,
  uploadedImages: [],
};

export default function editPostPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_STATE:
      return { ...initialState };

    case SHOW_POST_REQUEST:
      return {
        ...state,
        showPostInProgress: true,
        showPostError: null,
        postId: payload.postId,
      };
    case SHOW_POST_SUCCESS:
      return {
        ...state,
        showPostInProgress: false,
        showPostError: null,
      };
    case SHOW_POST_ERROR:
      return {
        ...state,
        showPostInProgress: false,
        showPostError: payload,
      };

    case UPDATE_POST_REQUEST:
      return {
        ...state,
        updatePostInProgress: true,
        updatePostError: null,
      };
    case UPDATE_POST_SUCCESS:
      return {
        ...state,
        updatePostInProgress: false,
        updatePostError: null,
      };
    case UPDATE_POST_ERROR:
      return {
        ...state,
        updatePostInProgress: false,
        updatePostError: payload,
      };

    case SUBMIT_POST_REQUEST:
      return {
        ...state,
        submitPostInProgress: true,
        submitPostError: null,
      };
    case SUBMIT_POST_SUCCESS:
      return {
        ...state,
        submitPostInProgress: false,
        submitPostError: null,
      };
    case SUBMIT_POST_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, submitPostInProgress: false, submitPostError: payload };

    case UPLOAD_IMAGE_REQUEST:
      return {
        ...state,
        uploadedImages: [...state.uploadedImages, payload.tempImage],
        imageUploadInProgress: true,
        imageUploadError: null,
      };
    case UPLOAD_IMAGE_SUCCESS:
      return {
        ...state,
        uploadedImages: state.uploadedImages.map(img =>
          img.id === payload.tempId ? { ...payload.image, originalId: payload.tempId } : img
        ),
        imageUploadInProgress: false,
        imageUploadError: null,
      };
    case UPLOAD_IMAGE_ERROR:
      return {
        ...state,
        imageUploadError: payload.error,
        imageUploadInProgress: false,
        uploadedImages: state.uploadedImages.map(img =>
          img.id === payload.tempId
            ? { ...img, isUploading: false, uploadError: payload.error }
            : img
        ),
      };
    case REMOVE_UPLOADED_IMAGE:
      return {
        ...state,
        uploadedImages: state.uploadedImages.filter(img =>
          img.file ? img.id !== payload : img.id.uuid !== payload
        ),
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialState = () => ({
  type: SET_INITIAL_STATE,
});

export const showPostRequest = postId => ({
  type: SHOW_POST_REQUEST,
  payload: { postId },
});
export const showPostSuccess = () => ({
  type: SHOW_POST_SUCCESS,
});
export const showPostError = error => ({
  type: SHOW_POST_ERROR,
  error: true,
  payload: error,
});

export const updatePostRequest = () => ({
  type: UPDATE_POST_REQUEST,
});
export const updatePostSuccess = () => ({
  type: UPDATE_POST_SUCCESS,
});
export const updatePostError = error => ({
  type: UPDATE_POST_ERROR,
  error: true,
  payload: error,
});

export const submitPostRequest = () => ({
  type: SUBMIT_POST_REQUEST,
});
export const submitPostSuccess = () => ({
  type: SUBMIT_POST_SUCCESS,
});
export const submitPostError = e => ({
  type: SUBMIT_POST_ERROR,
  error: true,
  payload: e,
});

export const uploadImageRequest = tempImage => ({
  type: UPLOAD_IMAGE_REQUEST,
  payload: { tempImage },
});
export const uploadImageSuccess = (image, tempId) => ({
  type: UPLOAD_IMAGE_SUCCESS,
  payload: { image, tempId },
});
export const uploadImageError = (error, tempId) => ({
  type: UPLOAD_IMAGE_ERROR,
  payload: { error, tempId },
});

export const removeUploadedImage = imageId => ({
  type: REMOVE_UPLOADED_IMAGE,
  payload: imageId,
});

// ================ Thunks ================ //

export const showPost = postId => async (dispatch, getState, sdk) => {
  dispatch(showPostRequest(postId));

  return sdk.ownListings
    .show({
      id: postId,
      include: ['images'],
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(showPostSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(showPostError(storableError(e)));
    });
};

export const updatePost = actionPayload => async (dispatch, getState, sdk) => {
  dispatch(updatePostRequest());

  try {
    const response = await sdk.ownListings.update(actionPayload, { expand: true });
    dispatch(addMarketplaceEntities(response));
    dispatch(updatePostSuccess());
    return response;
  } catch (e) {
    dispatch(updatePostError(storableError(e)));
    throw e;
  }
};

export const submitPost = actionPayload => async (dispatch, getState, sdk) => {
  dispatch(submitPostRequest());

  try {
    // Create the post first
    const response = await sdk.ownListings.create(actionPayload, { expand: true });
    dispatch(addMarketplaceEntities(response));

    const listingId = response.data.data.id;
    const listingTitle = response.data.data.attributes.title;

    // Approve the post immediately after creation
    try {
      const approveResponse = await listingsAPI.approve({ id: listingId.uuid });
      // Update marketplace entities with approved post data
      dispatch(addMarketplaceEntities(approveResponse));
      dispatch(submitPostSuccess());
      return { listingId, listingTitle, approveResponse };
    } catch (approveError) {
      console.error('Post created but approval failed:', approveError);
      // Still dispatch success since the post was created successfully
      dispatch(submitPostSuccess());
      return { listingId, listingTitle };
    }
  } catch (e) {
    dispatch(submitPostError(storableError(e)));
    throw e;
  }
};

export const uploadImage = (file, originalId) => (dispatch, getState, sdk) => {
  // Always use the originalId from the form state
  if (!originalId) {
    console.error('No originalId provided to uploadImage');
    return Promise.reject(new Error('No originalId provided'));
  }

  const tempImage = {
    id: originalId,
    file: file,
    isUploading: true,
    uploadError: null,
  };

  // Add temporary image to state
  dispatch(uploadImageRequest(tempImage));

  const queryParams = {
    expand: true,
  };

  // Resized in the browser first: uploads are billed by size.
  return prepareImageForUpload(file)
    .then(resized =>
      sdk.images.upload(
        {
          image: resized,
        },
        queryParams
      )
    )
    .then(response => {
      const image = response.data.data;

      // Replace temporary image with the uploaded one, keeping the same ID
      const uploadedImage = {
        ...image,
        isUploading: false,
        uploadError: null,
      };
      dispatch(uploadImageSuccess(uploadedImage, originalId));
      return uploadedImage;
    })
    .catch(error => {
      // Update the image with error state instead of removing it
      dispatch(uploadImageError(storableError(error), originalId));
      throw error;
    });
};

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  const { id, type } = params;

  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialState());

  if (type !== LISTING_PAGE_PARAM_TYPE_NEW) {
    return dispatch(showPost(id));
  }

  return Promise.resolve();
};
